import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Heading,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { withAuth0 } from '@auth0/auth0-react';

import BasePage from '../../components/BasePage';
import LoadingWrapper from '../../components/LoadingWrapper';
import PurchaseCardsSection from './PurchaseCardsSection';
import PurchaseSuccessModal from './PurchaseSuccessModal';
import { PaymentLink } from '../../types/interfaces';

interface PurchasePageState {
  isLoading: boolean;
  oneTimePaymentLinks: PaymentLink[];
  subscriptionPaymentLinks: PaymentLink[];
  error: string | null;
}

const PurchasePage = () => {
  const [state, setState] = useState<PurchasePageState>({
    isLoading: true,
    oneTimePaymentLinks: [],
    subscriptionPaymentLinks: [],
    error: null,
  });

  // Control the modal using the URL query parameter `purchase-success`
  // Note: This is slightly awkward, but I'm not sure what a better solution is.
  // Maybe the issue is that I'm using a modal in a way it wasn't intended.
  // I think it's fine for now, but it's something we may want to improve in the future.
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const purchaseSuccess = queryParams.get('purchase-success');

  const navigate = useNavigate();
  const handleClose = () => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.delete('purchase-success');
    navigate(`${window.location.pathname}?${queryParams.toString()}`);
  };

  useEffect(() => {
    fetch(`/data/${process.env.REACT_APP_PURCHASE_PAGE_DATA_FILE}`)
      .then((response) => response.json())
      .then((data) => {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          subscriptionPaymentLinks: data['subscriptionPaymentLinks'],
          oneTimePaymentLinks: data['oneTimePaymentLinks'],
        }));
        return data;
      })
      .catch((error) => {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
        console.error(error);
      });
  }, [setState]);

  return (
    <BasePage>
      <Heading as="h2" size="lg">
        Purchase CDR
      </Heading>
      <PurchaseSuccessModal
        isOpen={purchaseSuccess === 'true'}
        onClose={handleClose}
      />
      <Text pt="1vh" pb="2rem" fontSize="md" maxW="45rem">
        Make a carbon dioxide removal (CDR) purchase or a monthly subscription.
        For custom bundles in kgs or tonnes or to buy for a company or employee
        carbon removal program, contact us
        <Link href="https://zopeful.com/contact-us" isExternal color="teal.500">
          {' '}
          here.
        </Link>
      </Text>
      <Text pt="1vh" pb="2rem" fontSize="md" maxW="45rem">
        Prices are in US Dollars ($). If your local currency is Euros, British
        Pounds, Canadian or Australian Dollars, it'll be converted at checkout.
        Payments are secure and fully encrypted.
      </Text>
      <Tabs size="md" variant="enclosed">
        <TabList>
          <Tab>Monthly Subscriptions</Tab>
          <Tab>One-time Purchases</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <LoadingWrapper isLoading={state.isLoading}>
              <PurchaseCardsSection
                paymentLinks={state.subscriptionPaymentLinks}
              />
            </LoadingWrapper>
          </TabPanel>
          <TabPanel>
            <LoadingWrapper isLoading={state.isLoading}>
              <PurchaseCardsSection paymentLinks={state.oneTimePaymentLinks} />
            </LoadingWrapper>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </BasePage>
  );
};

export default withAuth0(PurchasePage);
